import { ctaDesc, ctaTitle } from "@/lib/seo";
// /en/industrial-estates/$slug — EN per-estate detail page
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { INDUSTRIAL_ESTATES, INDUSTRIAL_ESTATE_SLUGS, getIndustrialEstate } from "@/data/industrial-estates";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/industrial-estates/$slug")({
  beforeLoad: ({ params }) => {
    if (!INDUSTRIAL_ESTATE_SLUGS.includes(params.slug)) throw notFound();
  },
  head: ({ params }) => {
    const e = getIndustrialEstate(params.slug)!;
    const path = `/en/industrial-estates/${e.slug}`;
    const thPath = `/industrial-estates/${e.slug}`;
    const title = `${e.nameEn} — Notary · Translation · Work Permit · MFA | NYC`;
    const desc = `${e.highlightsEn} On-site courier from Bangkok and ISO 17100 EN/JP/CN/KR translation, MFA legalisation in 3 days, BOI work permits in 7-10 days. Free consultation, fast reply.`;
    const steps = [
      { name: `Site visit at ${e.nameEn}`, text: `NYC courier reaches your plant inside ${e.nameEn} (${e.provinceEn}, ${e.distanceToBkkKm} km from Bangkok). Nearest airport: ${e.nearbyAirport}.` },
      { name: "Scope + quote within 4 hours", text: `Separate quotes for notarisation, translation (${e.topNationalities.join(" / ")}), MFA legalisation and work permits — same pricing as Bangkok clients.` },
      { name: "Execute + status reporting", text: `Notarisation same-day, translation 1-2 days, MFA 2-3 days, work permit 7-10 days. Every milestone reported via LINE / email.` },
      { name: `Return to ${e.nameEn}`, text: `Direct courier back to your plant or free EMS Thailand-wide for orders above THB 5,000.` },
    ];
    const faqs = [
      { q: `Has NYC worked with plants inside ${e.nameEn}?`,
        a: e.highlightsEn },
      { q: `How fast is document pickup at ${e.nameEn}?`,
        a: `Same-day within 4 hours of confirmation. Driving time from Bangkok is roughly ${Math.ceil(e.distanceToBkkKm / 60)} hour(s); we also use regional branches when faster.` },
      { q: `Which languages do you support for ${e.nameEn}?`,
        a: `${e.topNationalities.join(" · ")} — ISO 17100-certified translators and on-site interpreters available.` },
      { q: `Are there extra fees for upcountry plants?`,
        a: `No — flat pricing matches Bangkok: notarisation from THB 1,500, translation THB 800/page, MFA legalisation THB 3,000/document.` },
      { q: `Work permit turnaround for new engineers at ${e.nameEn}?`,
        a: `BOI/IEAT One Stop Service 3-5 days · standard 7-10 business days. NYC handles Power of Attorney and full company packet.` },
      { q: `Nearest port and airport for ${e.nameEn}?`,
        a: `Port: ${e.nearbyPort} · Airport: ${e.nearbyAirport} · ${e.distanceToBkkKm} km from Bangkok.` },
    ];
    const breadcrumbs = [
      { name: "Home (EN)", path: "/en" },
      { name: "Industrial Estates", path: "/en/industrial-estates" },
      { name: e.nameEn, path },
    ];
    const localBusiness = {
      type: "application/ld+json" as const,
      children: JSON.stringify({
        "@context": "https://schema.org", "@type": "LegalService",
        "@id": `${SITE}${path}#local`,
        name: `NYC Legal & Notary — ${e.nameEn} Service`,
        url: `${SITE}${path}`, telephone: "+66922429293", priceRange: "฿฿",
        areaServed: [
          { "@type": "AdministrativeArea", name: e.provinceEn },
          { "@type": "Place", name: e.nameEn },
        ],
        address: { "@type": "PostalAddress", addressCountry: "TH", addressRegion: e.region, addressLocality: e.provinceEn },
        geo: { "@type": "GeoCoordinates", latitude: e.lat, longitude: e.lng },
        geoRadius: "50000",
        knowsLanguage: ["en", "th", "ja", "zh", "ko"],
      }),
    };
    return {
      meta: [
        { title: ctaTitle(title, "/en/industrial-estates/$slug", "en") }, { name: "description", content: ctaDesc(desc, "/en/industrial-estates/$slug", "en")},
        { name: "keywords", content: `${e.nameEn}, notary ${e.provinceEn}, work permit ${e.provinceEn}, technical translation Thailand, MFA legalisation ${e.nameEn}, BOI ${e.nameEn}` },
        { property: "og:title", content: ctaTitle(title, "/en/industrial-estates/$slug", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/industrial-estates/$slug", "en")},
        { property: "og:url", content: `${SITE}${path}` }, { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: `${SITE}${path}` },
        { rel: "alternate", hrefLang: "th-TH", href: `${SITE}${thPath}` },
        { rel: "alternate", hrefLang: "en", href: `${SITE}${path}` },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE}${thPath}` },
      ],
      scripts: [
        ...buildLandingSchemas({
          path, title, description: desc,
          serviceType: "Industrial Estate Legal & Translation Service",
          priceFromThb: 1500, steps, faqs, breadcrumbs,
        }),
        localBusiness,
      ],
    };
  },
  component: IndustrialEstateDetailEn,
});

function IndustrialEstateDetailEn() {
  const { slug } = Route.useParams();
  const e = getIndustrialEstate(slug)!;
  const nearby = INDUSTRIAL_ESTATES.filter((x) => x.region === e.region && x.slug !== e.slug).slice(0, 6);
  return (
    <>
      <ServiceLanding
        locale="en"
        badge={`Industrial Estate · ${e.provinceEn}`}
        title={`End-to-end legal & translation services at ${e.nameEn}`}
        subtitle={`${e.highlightsEn} Operator: ${e.operator} · ${e.zoneType} · Port ${e.nearbyPort} · Airport ${e.nearbyAirport}.`}
        quickAnswer={{
          q: `How does a plant inside ${e.nameEn} engage NYC?`,
          a: `Message LINE @nyc-notary or call +66 92-242-9293 — our courier reaches your ${e.nameEn} plant next business day; translation + notarisation delivered in 1-2 days, MFA legalisation in 3 days, work permits in 7-10 days. 99.6% first-time acceptance.`,
        }}
        keyFacts={[
          { k: "Operator", v: e.operator },
          { k: "Zone type", v: e.zoneType },
          { k: "Top industries", v: e.industries.slice(0, 3).join(" · ") },
          { k: "Foreign workforce origin", v: e.topNationalities.slice(0, 4).join(" · ") },
          { k: "Nearest port", v: e.nearbyPort },
          { k: "Nearest airport", v: e.nearbyAirport },
          { k: "Distance from Bangkok", v: `${e.distanceToBkkKm} km` },
          { k: "Starts at (notary)", v: "THB 1,500" },
        ]}
        heroBullets={[
          `On-site document collection at ${e.nameEn} every business day`,
          `Technical translation EN/JP/CN/KR · ISO 17100 quality cycle`,
          `Work Permit + BOI Visa for ${e.topNationalities.slice(0, 3).join(" / ")}`,
          `MFA + Embassy legalisation in 5-7 days`,
          `Coverage across all ${e.industries.length} sector clusters in-zone`,
        ]}
        whoFor={{
          th: [
            `HR Directors renewing engineer work permits inside ${e.nameEn}`,
            `Export teams needing monthly COO + MFA legalisation`,
            `BOI compliance officers filing annual reports and amendments`,
          ],
          foreign: [
            `Japanese/Chinese/Korean engineers renewing work permits at ${e.nameEn}`,
            `Plant managers needing certified translation of HR contracts and ISO manuals`,
            `Expat directors notarising Board Resolutions and POAs for ${e.nameEn} operations`,
          ],
        }}
        steps={[
          { name: `Site visit at ${e.nameEn}`, text: `Courier or EMS pickup from ${e.provinceEn}.` },
          { name: "Quote within 4 hours", text: `Split by notary / translation / MFA / work permit. Same Bangkok pricing.` },
          { name: "Execute + status", text: `Notary same-day · translation 1-2 days · MFA 2-3 days · work permit 7-10 days.` },
          { name: `Return to ${e.nameEn}`, text: `Direct courier or free EMS above THB 5,000.` },
        ]}
        pricing={[
          { label: "Notarisation (POA / Board Resolution)", price: "1,500 - 8,000", note: "Notarial Services Attorney" },
          { label: "Certified Translation EN/JP/CN/KR", price: "800 / page", note: "ISO 17100" },
          { label: "MFA Legalisation", price: "3,000 / document", note: "All-in" },
          { label: "New Work Permit (BOI/IEAT)", price: "12,000", note: "7-10 business days" },
          { label: "Apostille (Hague countries)", price: "5,000 / document", note: "Via MFA + Embassy" },
        ]}
        faqs={[
          { q: `Does NYC have a dedicated courier into ${e.nameEn}?`,
            a: `Yes — one morning and one evening run on the ${e.provinceEn}↔Bangkok route, daily.` },
          { q: `Are upcountry fees higher than Bangkok?`,
            a: `No — flat pricing with courier included in the job total.` },
          { q: `How fast can you turn around a work permit at ${e.nameEn}?`,
            a: `BOI/IEAT One Stop Service 3-5 days · standard 7-10 business days.` },
          { q: `Do you translate MSDS / HAZOP / ISO manuals?`,
            a: `Yes — translators with engineering backgrounds under ISO 17100 quality cycle.` },
        ]}
        relatedLinks={[
          { to: "/en/work-permit", label: "Work Permit + BOI Visa" },
          { to: "/en/translate", label: "Certified Translation EN/JP/CN/KR" },
          { to: "/en/translate/mfa", label: "MFA Legalisation" },
          { to: "/en/notary", label: "Notarial Services Attorney" },
          { to: "/en/apostille", label: "Apostille (Hague)" },
          { to: "/en/apec", label: "APEC Card for plant executives" },
        ]}
      />

      {nearby.length > 0 && (
        <section className="px-4 py-12">
          <div className="mx-auto max-w-5xl">
            <h2 className="text-2xl font-extrabold sm:text-3xl">Nearby estates in {e.region}</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {nearby.map((n) => (
                <Link key={n.slug} to="/en/industrial-estates/$slug" params={{ slug: n.slug }}
                  className="rounded-xl border border-border bg-card p-4 hover:border-primary"
                  data-track-zone="industrial-detail-nearby-en">
                  <div className="font-bold">{n.nameEn}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{n.provinceEn} · {n.zoneType}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
